import React, { useState, useRef, useEffect } from "react";
import { FileSignature, Printer, Send, Download } from "lucide-react";
import { getSystemConfig } from "../services/systemConfigService";
import html2pdf from "html2pdf.js";

// ─── Build the same itemized equipment list used in the Rental Agreement's
// PRODUCTS table (manufacturer / model / description), comma separated ──────
const HARDWARE_DESCRIPTIONS = {
  "T54W": "YEALINK T54W - IP DESK PHONE HANDSET",
  "T57W": "YEALINK T57W - IP DESK PHONE HANDSET",
  "T58W": "YEALINK T58W - IP DESK PHONE HANDSET",
  "YEALINK T85": "YEALINK T85 - IP DESK PHONE HANDSET",
  "YEALINK T87": "YEALINK T87 - IP DESK PHONE HANDSET",
  "YEALINK T88": "YEALINK T88 - IP DESK PHONE HANDSET",
  "Grandstream 3470": "GRANDSTREAM 3470 - IP DESK PHONE HANDSET",
  "Grandstream 3480": "GRANDSTREAM 3480 - IP DESK PHONE HANDSET",
  "Cisco 9871": "CISCO 9871 - IP DESK PHONE HANDSET",
  "Yealink W73H DECT": "YEALINK W73H DECT - IP DECT CORDLESS PHONE HANDSET",
  "UCM IP PBX": "UCM PBX - UCM IP PBX",
  "Draytek 2865LAC": "DRAYTEK 2865LAC ROUTER",
  "Netgear GS108LP": "NETGEAR GS108LP SWITCH",
};

// Fixed handset order, matching the Rental Agreement's PRODUCTS table rows
const HARDWARE_ORDER = [
  "T54W",
  "T57W",
  "T58W",
  "YEALINK T88",
  "YEALINK T85",
  "YEALINK T87",
  "Grandstream 3470",
  "Grandstream 3480",
  "Cisco 9871",
  "Yealink W73H DECT",
];

// These two are *included* whenever quantity + focQuantity > 0, but the
// Rental Agreement's table only ever prints item.quantity as the count —
// mirroring that same (slightly quirky) behaviour here.
const QUANTITY_ONLY_ITEMS = ["Cisco 9871", "Yealink W73H DECT"];

// Fixed order for the additional-hardware line items
const ADDITIONAL_HARDWARE_ORDER = ["UCM IP PBX", "Draytek 2865LAC", "Netgear GS108LP"];

const buildEquipmentList = (hardwareItems = [], additionalHardware = []) => {
  const parts = [];

  const findItem = (list, name) => list.find((i) => i.name === name) || { quantity: 0, focQuantity: 0 };
  const totalQty = (item) => (item.quantity || 0) + (item.focQuantity || 0);

  const mobileAppUsers = hardwareItems.reduce((total, item) => {
    if (item.name === "Mobile App" || item.name === "Mobile App Addon") {
      return total + (item.quantity || 0) + (item.focQuantity || 0);
    }
    return total;
  }, 0);

  // 1. Mobile App — first row on the Rental Agreement
  if (mobileAppUsers > 0) {
    parts.push(`${mobileAppUsers}x GAMMA MOBILE - MOBILE CLIENT APPLICATION`);
  }

  // 2. Handsets, in the same fixed order as the Rental Agreement
  HARDWARE_ORDER.forEach((name) => {
    const item = findItem(hardwareItems, name);
    const qty = totalQty(item);
    if (qty > 0) {
      const displayQty = QUANTITY_ONLY_ITEMS.includes(name) ? (item.quantity || 0) : qty;
      const desc = HARDWARE_DESCRIPTIONS[name] || name;
      parts.push(`${displayQty}x ${desc}`);
    }
  });

  // 3. Additional hardware (UCM / router / switch) — quantity only, no FOC
  ADDITIONAL_HARDWARE_ORDER.forEach((name) => {
    const item = findItem(additionalHardware, name);
    const qty = item.quantity || 0;
    if (qty > 0) {
      const desc = HARDWARE_DESCRIPTIONS[name] || name;
      parts.push(`${qty}x ${desc}`);
    }
  });

  // 4. Fixed line items, always/conditionally present
  parts.push("1x FIBRE INSTALLATION - PHONE SYSTEM INSTALLATION");
  if (mobileAppUsers > 0) {
    parts.push(`${mobileAppUsers}x GAMMA LICENCE - LIFETIME LICENCE`);
    parts.push(`${mobileAppUsers}x TOLLRING BUSINESS - CALL REPORTING`);
    parts.push(`${mobileAppUsers}x TOLLRING BUSINESS - CALL RECORDING`);
  }

  return parts.join(", ");
};

// ─── Shared style helpers (same pattern as AssignmentLetterTab) ───────────────
const inputCls =
  "border border-gray-400 rounded px-1.5 py-0.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500";
const inlineInput = (extraCls = "") => `${inputCls} inline-block ${extraCls}`;

const pdfBox = (val, { minW = 120, flex = false, bold = false, multiline = false } = {}) => ({
  display: flex ? "block" : "inline-block",
  border: "1px solid #9ca3af",
  borderRadius: 4,
  background: "#f8fafc",
  padding: multiline ? "6px 10px" : "2px 8px",
  ...(flex
    ? { flex: "1 1 auto", minWidth: 0, width: "100%" }
    : { minWidth: minW }),
  maxWidth: "100%",
  minHeight: multiline ? 60 : 26,
  fontSize: 13,
  fontWeight: bold ? 600 : 400,
  color: val ? "#111827" : "#9ca3af",
  fontFamily: "Arial, Helvetica, sans-serif",
  whiteSpace: multiline ? "pre-wrap" : "normal",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  verticalAlign: "middle",
  lineHeight: multiline ? 1.6 : "22px",
  boxSizing: "border-box",
});

// ─── Date helpers ───────────────────────────────────────────────────────────
const ddmmyyyyToISO = (str) => {
  if (!str) return "";
  const parts = str.split("/");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  if (!d || !m || !y) return "";
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

// ─── DatePicker ─────────────────────────────────────────────────────────────
const DatePicker = ({ value, onChange, placeholder = "dd/mm/yyyy", className = "", isPdfMode = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const inputRef = useRef(null);
  const isoValue = ddmmyyyyToISO(value);

  if (isPdfMode) {
    return (
      <span style={pdfBox(value, { minW: 130 })}>
        {value || <span style={{ color: "#9ca3af" }}>dd/mm/yyyy</span>}
      </span>
    );
  }

  const handleDateChange = (e) => {
    onChange(isoToDDMMYYYY(e.target.value));
    setShowPicker(false);
  };

  return (
    <span className="relative inline-block" style={{ verticalAlign: "middle" }}>
      <input
        className={`${inputCls} pr-7 ${className}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowPicker(true)}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => {
          setShowPicker((s) => !s);
          setTimeout(() => inputRef.current?.showPicker?.(), 50);
        }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500 focus:outline-none"
        aria-label="Open date picker"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      <input
        ref={inputRef}
        type="date"
        value={isoValue}
        onChange={handleDateChange}
        onBlur={() => setShowPicker(false)}
        className="absolute inset-0 opacity-0 pointer-events-none w-full h-full"
        tabIndex={-1}
        style={{ zIndex: showPicker ? 10 : -1, pointerEvents: showPicker ? "auto" : "none" }}
      />
    </span>
  );
};

// ─── Agreement No. digit boxes ────────────────────────────────────────────────
const AgreementNoBoxes = ({ value, onChange, isPdfMode }) => {
  const digits = (value || "").padEnd(9, " ").slice(0, 9).split("");

  const handleDigitChange = (idx, char) => {
    const clean = char.replace(/[^a-zA-Z0-9]/g, "").slice(-1);
    const arr = (value || "").padEnd(9, " ").split("");
    arr[idx] = clean || " ";
    onChange(arr.join("").replace(/\s+$/, ""));
  };

  return (
    <div style={{ display: "flex", gap: 2 }}>
      {digits.map((d, idx) => (
        <span
          key={idx}
          style={{
            width: isPdfMode ? 24 : 26,
            height: isPdfMode ? 24 : 26,
            border: "1px solid #1f2937",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontFamily: "Arial, Helvetica, sans-serif",
            background: "#fff",
            color: "#111827",
            boxSizing: "border-box",
          }}
        >
          {isPdfMode ? (
            d.trim() || ""
          ) : (
            <input
              value={d.trim()}
              onChange={(e) => handleDigitChange(idx, e.target.value)}
              maxLength={1}
              style={{
                width: "100%",
                height: "100%",
                textAlign: "center",
                border: "none",
                outline: "none",
                fontSize: 13,
                background: "transparent",
              }}
            />
          )}
        </span>
      ))}
    </div>
  );
};

// ─── PDF document wrapper style ───────────────────────────────────────────────
const pdfDocStyle = {
  background: "#ffffff",
  padding: "40px 48px",
  fontFamily: "Arial, Helvetica, sans-serif",
  fontSize: 13,
  color: "#1a1a1a",
  lineHeight: 1.6,
  maxWidth: 780,
  margin: "0 auto",
};

// ─── Header ─────────────────────────────────────────────────────────────────
const CertHeader = ({ data, onChange, isPdfMode }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 20,
      paddingBottom: 14,
      borderBottom: "2px solid #1f2937",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <img
        src="https://quoting-tool.fibretelecomsuk.co.uk/images/logo-vertical.jpg"
        alt="Fibre Telecoms UK"
        style={{ width: isPdfMode ? 96 : 104, height: "auto", objectFit: "contain" }}
        crossOrigin="anonymous"
      />
      <h2 style={{ fontSize: isPdfMode ? 24 : 26, fontWeight: 400, color: "#1f2937", margin: 0 }}>
        Certificate of Acceptance
      </h2>
    </div>
    <div style={{ textAlign: "right" }}>
      <div style={{ fontSize: 12, color: "#374151", marginBottom: 6 }}>Agreement No.</div>
      <AgreementNoBoxes
        value={data.agreementNo}
        onChange={(v) => onChange("agreementNo", v)}
        isPdfMode={isPdfMode}
      />
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Certificate of Acceptance document
// ═══════════════════════════════════════════════════════════════════════════════
const CertificateOfAcceptance = ({ data, onChange, isPdfMode }) => {
  const B = ({ v, minW = 160, multiline = false, flex = false }) => (
    <span style={pdfBox(v, { minW, multiline, flex })}>
      {v || <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 11 }}>—</span>}
    </span>
  );

  const blueBox = {
    background: "#2486b8",
    color: "#ffffff",
    padding: "14px 18px",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 0,
  };

  const fieldRow = { display: "flex", alignItems: "center", gap: 10, marginBottom: 4, minWidth: 0 };
  const fieldLabel = { minWidth: 150, flexShrink: 0, color: "#ffffff", fontSize: 13, fontWeight: 600 };

  const inner = (
    <>
      <CertHeader data={data} onChange={onChange} isPdfMode={isPdfMode} />

      <div style={{ fontSize: 13, marginBottom: 16 }}>
        <div>To The Owner</div>
        <div style={{ color: "#374151" }}>To be signed on behalf of the Hirer only after taking delivery of the equipment.</div>
      </div>

      <div style={{ border: "1px solid #1f2937", borderRadius: 4, padding: "14px 18px", marginBottom: 20, fontSize: 12.5, lineHeight: 1.7 }}>
        <p style={{ margin: "0 0 8px" }}>By signing this Certificate of Acceptance the signatory below hereby;</p>
        <div style={{ display: "flex", gap: 10, marginBottom: 6 }}>
          <span style={{ minWidth: 18 }}>(i)</span>
          <span>acknowledges and confirms that the Hirer has taken delivery of the equipment specified in the Hire Agreement in good order and condition and which accords with the Hirer's specifications and requirements;</span>
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 6 }}>
          <span style={{ minWidth: 18 }}>(ii)</span>
          <span>authorises The Owner to pay the purchase price of the equipment to the Supplier relying upon the contents of this Certificate being true and accurate; and</span>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <span style={{ minWidth: 18 }}>(iii)</span>
          <span>warrants and represents to The Owner that, having made all necessary enquiries, the Hirer has the power and capacity to enter into the Hire Agreement and that the signatory is duly authorised to sign the Hire Agreement on behalf of the Hirer.</span>
        </div>
      </div>

      {/* Blue panel: Equipment details, location, signature block */}
      <div style={{ background: "#2486b8", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px" }}>
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Details of Equipment</div>
          {isPdfMode ? (
            <div
              style={{
                ...pdfBox(data.equipmentDetails, { flex: true, multiline: true }),
                background: "#ffffff",
              }}
            >
              {data.equipmentDetails || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>}
            </div>
          ) : (
            <textarea
              className={`${inputCls} w-full h-16 resize-none`}
              value={data.equipmentDetails}
              onChange={(e) => onChange("equipmentDetails", e.target.value)}
              placeholder="Auto-filled from the lease's equipment list (comma separated)"
            />
          )}
        </div>

        <div style={{ padding: "0 18px 14px" }}>
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>The Equipment is located at</div>
          {isPdfMode ? (
            <div
              style={{
                ...pdfBox(data.equipmentLocation, { flex: true, multiline: true }),
                background: "#ffffff",
              }}
            >
              {data.equipmentLocation || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>}
            </div>
          ) : (
            <textarea
              className={`${inputCls} w-full h-16 resize-none`}
              value={data.equipmentLocation}
              onChange={(e) => onChange("equipmentLocation", e.target.value)}
              placeholder="Auto-filled from the lease's site address"
            />
          )}
        </div>

        <div style={{ padding: "0 18px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <div style={{ color: "#fff", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Signature of (or on behalf of) Hirer</div>
            {/* Adobe Sign target: a .signature-section wrapping a .signature-field.adobe-sig
                containing the {{SigN_es_:signerN:signature}} text tag, exactly the pattern
                used on the Rental Agreement pages, so Adobe Sign can detect and convert
                this into a real signature field when the document is sent for e-signature. */}
            <div
              className="signature-section"
              data-section="coa_signature"
              style={{
                background: "#ffffff",
                borderRadius: 4,
                minHeight: 96,
                border: "1px solid #9ca3af",
              }}
            >
              <div
                className="signature-field adobe-sig"
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 96,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9ca3af",
                  fontSize: 11,
                }}
              >
                {"{{Sig1_es_:signer1:signature}}"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "flex-start", paddingTop: 2, minWidth: 0 }}>
            <div style={fieldRow}>
              <span style={fieldLabel}>Company Name</span>
              {isPdfMode ? (
                <B v={data.nameOfHirer} flex />
              ) : (
                <input
                  className={`${inputCls} flex-1 bg-white`}
                  value={data.nameOfHirer}
                  onChange={(e) => onChange("nameOfHirer", e.target.value)}
                  placeholder="Company name"
                />
              )}
            </div>
            <div style={fieldRow}>
              <span style={fieldLabel}>Name of Signatory</span>
              {isPdfMode ? (
                <B v={data.nameOfSignatory} flex />
              ) : (
                <input
                  className={`${inputCls} flex-1 bg-white`}
                  value={data.nameOfSignatory}
                  onChange={(e) => onChange("nameOfSignatory", e.target.value)}
                  placeholder="Auto-filled from MD name"
                />
              )}
            </div>
            <div style={fieldRow}>
              <span style={fieldLabel}>Position of Signatory</span>
              {isPdfMode ? (
                <B v={data.positionOfSignatory} flex />
              ) : (
                <input
                  className={`${inputCls} flex-1 bg-white`}
                  value={data.positionOfSignatory}
                  onChange={(e) => onChange("positionOfSignatory", e.target.value)}
                  placeholder="Auto-filled from role"
                />
              )}
            </div>
            <div style={fieldRow}>
              <span style={fieldLabel}>Email Address</span>
              {isPdfMode ? (
                <B v={data.email} flex />
              ) : (
                <input
                  className={`${inputCls} flex-1 bg-white`}
                  value={data.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  placeholder="Auto-filled from director/billing email"
                />
              )}
            </div>
            <div style={fieldRow}>
              <span style={fieldLabel}>Date</span>
              {isPdfMode ? (
                <span style={pdfBox(data.date, { minW: 140 })}>
                  {data.date || <span style={{ color: "#9ca3af" }}>dd/mm/yyyy</span>}
                </span>
              ) : (
                <DatePicker value={data.date} onChange={(v) => onChange("date", v)} className="w-40" />
              )}
            </div>
          </div>
        </div>
      </div>

      <p style={{ fontSize: 10.5, color: "#374151", marginTop: 16, fontWeight: 600 }}>
        THE HIRER ACCEPTS THE EQUIPMENT FOR THE FULL PERIOD OF RENTAL AND ACKNOWLEDGES THAT THERE IS NO TRIAL PERIOD OR FREE MAINTENANCE INCLUDED.
      </p>
    </>
  );

  if (isPdfMode) {
    return <div style={pdfDocStyle}>{inner}</div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm font-sans text-sm leading-relaxed">
      {inner}
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════════════════
// MAIN — CertificateOfAcceptanceTab
// ═══════════════════════════════════════════════════════════════════════════════
const CertificateOfAcceptanceTab = ({ selectedConnectionId }) => {
  const [isSending, setIsSending] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGeneratingAdobePDF, setIsGeneratingAdobePDF] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [isPdfMode, setIsPdfMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const docRef = useRef(null);

  const [data, setData] = useState({
    agreementNo: "",
    equipmentDetails: "",
    equipmentLocation: "",
    nameOfHirer: "",
    nameOfSignatory: "",
    positionOfSignatory: "",
    email: "",
    date: "",
  });

  const patch = (k, v) => setData((p) => ({ ...p, [k]: v }));

  // ─── Auto-populate equipment list & address from the lease/installation record ──
  // Agreement No. is intentionally left blank here — it's a manual entry filled
  // in (letter by letter, in the boxes) after the document has been generated.
  useEffect(() => {
    if (!selectedConnectionId) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data: cfg } = await getSystemConfig(selectedConnectionId);
        if (!cfg) return;

        const fullAddress = [
          cfg.connection_name,
          cfg.registered_address_line_1,
          cfg.registered_address_line_2,
          cfg.registered_address_line_3,
          cfg.postcode,
        ]
          .filter(Boolean)
          .join(", ");

        const hwItems = Array.isArray(cfg.hardwareItems) ? cfg.hardwareItems : [];
        const addlHw = cfg.additionalHardware
          ? Array.isArray(cfg.additionalHardware)
            ? cfg.additionalHardware
            : Object.values(cfg.additionalHardware)
          : [];
        const equipmentList = buildEquipmentList(hwItems, addlHw);

        setData((p) => ({
          ...p,
          equipmentDetails: equipmentList,
          equipmentLocation: fullAddress,
          nameOfHirer: cfg.connection_name || p.nameOfHirer,
          nameOfSignatory: cfg.md_name || p.nameOfSignatory,
          positionOfSignatory: cfg.position || cfg.role || p.positionOfSignatory,
          email: cfg.director_email || cfg.billing_email || p.email,
        }));
      } catch (err) {
        console.error("CertificateOfAcceptanceTab: failed to load config", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedConnectionId]);


  // ─── Download PDF ─────────────────────────────────────────────────────────
 const handleDownloadPdf = async () => {
  setIsDownloading(true);
  setIsPdfMode(true);
  await new Promise((r) => setTimeout(r, 300)); // let inputs swap to static text

  try {
    const element = docRef.current.cloneNode(true);
    element.querySelectorAll(".no-pdf-export").forEach((el) => el.remove());

    const options = {
      margin: [10, 10, 10, 10],
      filename: `certificate-of-acceptance-${selectedConnectionId || data.agreementNo || "document"}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
        hotfixes: ["px_scaling"],
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    await html2pdf().set(options).from(element).save();
  } catch (err) {
    console.error("PDF download failed:", err);
    alert("Error generating PDF. Please try again.");
  } finally {
    setIsPdfMode(false);
    setIsDownloading(false);
  }
};

  // ─── Send ───────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    setIsSending(true);
    setSendStatus(null);
    setIsPdfMode(true);
    await new Promise((r) => setTimeout(r, 300));
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("authToken");
      const html = docRef.current?.innerHTML || "";

      const res = await fetch(`${API_BASE_URL}/certificate-of-acceptance/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          connection_id: selectedConnectionId,
          html,
          data,
        }),
      });

      if (res.ok) {
        setSendStatus({ type: "success", message: "Certificate of Acceptance sent successfully." });
      } else {
        const err = await res.json().catch(() => ({}));
        setSendStatus({ type: "error", message: err?.message || "Failed to send. Please try again." });
      }
    } catch {
      setSendStatus({ type: "error", message: "Network error. Please check your connection." });
    } finally {
      setIsPdfMode(false);
      setIsSending(false);
    }
  };

  // ─── Adobe e-Signature ──────────────────────────────────────────────────
  // Mirrors RentalAgreementTab's sendToAdobeSign: builds the Certificate of
  // Acceptance HTML (with the {{Sig1_es_:signer1:signature}} text tag left
  // intact so Adobe Sign can detect and convert it into a real signature
  // field), then posts it to the same /pdf/generate-and-send endpoint.
  const sendToAdobeSign = async () => {
    if (!data.email) {
      alert("Please enter a customer email address before sending for signature.");
      return;
    }
    if (!window.confirm(`Send the Certificate of Acceptance to: ${data.email}\n\nDo you want to continue?`)) return;

    setIsGeneratingAdobePDF(true);
    setIsPdfMode(true);
    await new Promise((r) => setTimeout(r, 300));

    try {
      const clone = docRef.current.cloneNode(true);
      clone.querySelectorAll(".no-pdf-export").forEach((el) => el.remove());

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${clone.innerHTML}
        </body>
        </html>
      `;

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing. Please log in again.");
      }

      const body = {
        html: htmlContent,
        signer_email: data.email,
        signer_name: data.nameOfSignatory || data.nameOfHirer,
        agreement_name: `Certificate of Acceptance - ${data.nameOfHirer || "Customer"}`,
        email_subject: `Certificate of Acceptance for ${data.nameOfHirer || "your company"}`,
        email_body: `Dear ${data.nameOfSignatory || "Customer"},\n\nPlease review and sign the attached Certificate of Acceptance.\n\nThank you.`,
        connection_id: selectedConnectionId,
        has_mobile_data: 0,
        has_rental: 0,
        sandbox: true,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/pdf/generate-and-send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        },
      );

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        console.error("Non-JSON response received:", textResponse.substring(0, 500));
        throw new Error(`Server returned non-JSON response (Status ${response.status}).`);
      }

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || "Failed to send Certificate of Acceptance");
      }

      setSendStatus({ type: "success", message: "Certificate of Acceptance sent for Adobe e-signature." });
    } catch (error) {
      console.error("Adobe Send Error (Certificate of Acceptance):", error);
      setSendStatus({ type: "error", message: `Error sending for signature: ${error.message}` });
    } finally {
      setIsGeneratingAdobePDF(false);
      setIsPdfMode(false);
    }
  };

  // ─── Loading state ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <svg className="animate-spin h-6 w-6 mr-3 text-blue-600" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading certificate data…
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-6">
      {!isPdfMode && (
        <div className="flex justify-end gap-3 pt-2 pb-8">
        
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
              isDownloading
                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                : "border-blue-300 text-blue-700 hover:bg-blue-50"
            }`}
          >
            <Download className="h-4 w-4" />
            {isDownloading ? "Generating PDF…" : "Download PDF"}
          </button>
          <button
            onClick={sendToAdobeSign}
            disabled={isGeneratingAdobePDF}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${
              isGeneratingAdobePDF
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 active:bg-green-800"
            }`}
          >
            <Send className="h-4 w-4" />
            {isGeneratingAdobePDF ? "Sending…" : "Adobe e-Signature"}
          </button>
          
        </div>
      )}
      {!isPdfMode && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
            <FileSignature className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-blue-900">Certificate of Acceptance</h4>
            <p className="text-xs text-blue-500">Edit any field directly on the document below, then download, send, or request an Adobe e-signature.</p>
          </div>
        </div>
      )}

      <div ref={docRef}>
        <CertificateOfAcceptance data={data} onChange={patch} isPdfMode={isPdfMode} />
      </div>

      {sendStatus && !isPdfMode && (
        <div
          className={`flex items-start gap-3 p-4 rounded-lg border text-sm ${
            sendStatus.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <span className="text-lg leading-none shrink-0">
            {sendStatus.type === "success" ? "✓" : "✕"}
          </span>
          <p>{sendStatus.message}</p>
        </div>
      )}

      {!isPdfMode && (
        <div className="flex justify-end gap-3 pt-2 pb-8">
        
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
              isDownloading
                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                : "border-blue-300 text-blue-700 hover:bg-blue-50"
            }`}
          >
            <Download className="h-4 w-4" />
            {isDownloading ? "Generating PDF…" : "Download PDF"}
          </button>
          <button
            onClick={sendToAdobeSign}
            disabled={isGeneratingAdobePDF}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${
              isGeneratingAdobePDF
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 active:bg-green-800"
            }`}
          >
            <Send className="h-4 w-4" />
            {isGeneratingAdobePDF ? "Sending…" : "Adobe e-Signature"}
          </button>
          
        </div>
      )}
    </div>
  );
};

export default CertificateOfAcceptanceTab;